import React, { useEffect, useState, useCallback } from 'react';
import { useProdAuth } from '../../contexts/ProdAuthContext';

const ACCENT = '#4A7C59';

const DUTIES = [
  { value: 'dryer',        label: 'Dryer',         desc: 'Turns and monitors coffee on drying beds' },
  { value: 'sorter',       label: 'Sorter',         desc: 'Sorts cherry or parchment by quality' },
  { value: 'cherry_washer',label: 'Cherry Washer',  desc: 'Washes and floats cherry' },
  { value: 'fermentation', label: 'Fermentation',   desc: 'Monitors fermentation tanks' },
  { value: 'porter',       label: 'Porter',         desc: 'Carries loads within the hub' },
  { value: 'loader',       label: 'Loader',         desc: 'Loads/unloads vehicles' },
  { value: 'security',     label: 'Security',       desc: 'Hub security' },
  { value: 'supervisor',   label: 'Supervisor',     desc: 'Oversees a work area or shift' },
  { value: 'general',      label: 'General',        desc: 'General labour' },
];

const DUTY_MAP: Record<string, string> = Object.fromEntries(DUTIES.map(d => [d.value, d.label]));

const DUTY_COLORS: Record<string, string> = {
  dryer:         'bg-orange-100 text-orange-700',
  sorter:        'bg-blue-100 text-blue-700',
  cherry_washer: 'bg-cyan-100 text-cyan-700',
  fermentation:  'bg-purple-100 text-purple-700',
  porter:        'bg-yellow-100 text-yellow-700',
  loader:        'bg-amber-100 text-amber-700',
  security:      'bg-red-100 text-red-700',
  supervisor:    'bg-green-100 text-green-700',
  general:       'bg-neutral-100 text-neutral-600',
};

const STATUS_COLORS: Record<string, string> = {
  active:   'bg-green-100 text-green-700',
  seasonal: 'bg-blue-100 text-blue-700',
  inactive: 'bg-neutral-100 text-neutral-500',
};

interface HubStaffMember {
  id: string;
  name: string;
  phone: string | null;
  idNumber: string | null;
  duty: string;
  status: string;
  notes: string;
  startDate: string | null;
  createdAt: string;
  centreName: string | null;
}

const emptyForm = {
  name: '', phone: '', idNumber: '', duty: 'general',
  status: 'active', notes: '', startDate: '',
};

export const HubStaffCRM: React.FC = () => {
  const { user: currentUser } = useProdAuth();
  const [staff, setStaff] = useState<HubStaffMember[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDuty, setFilterDuty] = useState<string>('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [editMember, setEditMember] = useState<HubStaffMember | null>(null);
  const [editForm, setEditForm] = useState({ ...emptyForm });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const canWrite = currentUser && ['administration', 'admin', 'super_admin'].includes(currentUser.role);
  const canDelete = currentUser && ['admin', 'super_admin'].includes(currentUser.role);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterDuty) params.set('duty', filterDuty);
      const res = await fetch(`/api/production/hub-staff?${params}`, { credentials: 'include' });
      const data = await res.json();
      setStaff(data.staff || []);
      setTotal(data.total || 0);
    } catch {
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDuty]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const res = await fetch('/api/production/hub-staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) { setFormError(d.error || 'Failed'); return; }
      setShowForm(false);
      setForm({ ...emptyForm });
      fetchStaff();
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (m: HubStaffMember) => {
    setEditMember(m);
    setEditForm({
      name: m.name, phone: m.phone || '', idNumber: m.idNumber || '',
      duty: m.duty, status: m.status, notes: m.notes,
      startDate: m.startDate || '',
    });
    setEditError('');
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editMember) return;
    setEditSaving(true);
    setEditError('');
    try {
      const res = await fetch(`/api/production/hub-staff?id=${editMember.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      if (!res.ok) { const d = await res.json(); setEditError(d.error || 'Failed'); return; }
      setEditMember(null);
      fetchStaff();
    } finally {
      setEditSaving(false);
    }
  };

  const toggleStatus = async (m: HubStaffMember) => {
    const newStatus = m.status === 'active' ? 'inactive' : 'active';
    const res = await fetch(`/api/production/hub-staff?id=${m.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status: newStatus }),
    });
    if (res.ok) setStaff(s => s.map(x => x.id === m.id ? { ...x, status: newStatus } : x));
  };

  const handleDelete = async (id: string) => {
    const res = await fetch(`/api/production/hub-staff?id=${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { setStaff(s => s.filter(x => x.id !== id)); setConfirmDeleteId(null); }
  };

  const inputCls = "w-full px-4 py-2.5 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none";
  const foc = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`);
  const blu = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    (e.target.style.boxShadow = '');

  const activeCount = staff.filter(s => s.status === 'active').length;
  const seasonalCount = staff.filter(s => s.status === 'seasonal').length;

  // Group by duty when no filter active
  const grouped = DUTIES.reduce<Record<string, HubStaffMember[]>>((acc, d) => {
    const inDuty = staff.filter(m => m.duty === d.value);
    if (inDuty.length > 0) acc[d.value] = inDuty;
    return acc;
  }, {});

  const StaffForm: React.FC<{
    values: typeof emptyForm;
    onChange: (f: typeof emptyForm) => void;
    onSubmit: (e: React.FormEvent) => void;
    onCancel: () => void;
    saving: boolean;
    error: string;
    title: string;
    submitLabel: string;
  }> = ({ values, onChange, onSubmit, onCancel, saving, error, title, submitLabel }) => (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{title}</h3>
          <button onClick={onCancel} className="text-[#A3A3A3] hover:text-[#1A1A1A]">✕</button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              Full name <span className="text-red-500">*</span>
            </label>
            <input type="text" value={values.name} required
              onChange={e => onChange({ ...values, name: e.target.value })}
              placeholder="e.g. John Otieno"
              className={inputCls} onFocus={foc} onBlur={blu} />
          </div>

          {/* Phone + ID side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Phone</label>
              <input type="tel" value={values.phone}
                onChange={e => onChange({ ...values, phone: e.target.value })}
                placeholder="07xx xxx xxx"
                className={inputCls} onFocus={foc} onBlur={blu} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">ID number</label>
              <input type="text" value={values.idNumber}
                onChange={e => onChange({ ...values, idNumber: e.target.value })}
                placeholder="National ID"
                className={inputCls} onFocus={foc} onBlur={blu} />
            </div>
          </div>

          {/* Duty */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Duty / role</label>
            <select value={values.duty} onChange={e => onChange({ ...values, duty: e.target.value })}
              className={inputCls} onFocus={foc} onBlur={blu}>
              {DUTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            {values.duty && (
              <p className="mt-1 text-xs text-[#A3A3A3]">{DUTIES.find(d => d.value === values.duty)?.desc}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Employment status</label>
            <div className="flex gap-2">
              {[
                { value: 'active', label: 'Active', hint: 'On payroll' },
                { value: 'seasonal', label: 'Seasonal', hint: 'Peak season only' },
                { value: 'inactive', label: 'Inactive', hint: 'Not currently working' },
              ].map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => onChange({ ...values, status: s.value })}
                  className="flex-1 py-2 text-xs font-medium rounded-lg border transition-colors"
                  style={values.status === s.value
                    ? { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT }
                    : { borderColor: '#E8E6E2', color: '#6B6B6B' }}
                  title={s.hint}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Start date */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Start date</label>
            <input type="date" value={values.startDate}
              onChange={e => onChange({ ...values, startDate: e.target.value })}
              className={inputCls} onFocus={foc} onBlur={blu} />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Notes</label>
            <textarea value={values.notes} rows={2}
              onChange={e => onChange({ ...values, notes: e.target.value })}
              placeholder="Any relevant notes..."
              className={`${inputCls} resize-none`}
              onFocus={foc} onBlur={blu} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel}
              className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}>
              {saving ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Hub Staff</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">
            {total} registered · {activeCount} active
            {seasonalCount > 0 && ` · ${seasonalCount} seasonal`}
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => { setForm({ ...emptyForm }); setFormError(''); setShowForm(true); }}
            className="px-4 py-2.5 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: ACCENT }}
          >
            + Add member
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {/* Status filters */}
        <div className="flex gap-1.5 bg-white border border-[#E8E6E2] rounded-lg p-1">
          {[{ value: '', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'seasonal', label: 'Seasonal' }, { value: 'inactive', label: 'Inactive' }].map(f => (
            <button key={f.value}
              onClick={() => setFilterStatus(f.value)}
              className="px-3 py-1 text-xs font-medium rounded-md transition-colors"
              style={filterStatus === f.value
                ? { backgroundColor: ACCENT, color: 'white' }
                : { color: '#6B6B6B' }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Duty filter */}
        <select
          value={filterDuty}
          onChange={e => setFilterDuty(e.target.value)}
          className="px-3 py-1.5 text-xs border border-[#E8E6E2] rounded-lg bg-white text-[#6B6B6B] focus:outline-none"
        >
          <option value="">All duties</option>
          {DUTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>

      {/* Create modal */}
      {showForm && (
        <StaffForm
          values={form}
          onChange={setForm}
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          saving={saving}
          error={formError}
          title="Add staff member"
          submitLabel="Add member"
        />
      )}

      {/* Edit modal */}
      {editMember && (
        <StaffForm
          values={editForm}
          onChange={setEditForm}
          onSubmit={handleEditSave}
          onCancel={() => setEditMember(null)}
          saving={editSaving}
          error={editError}
          title={editMember.name}
          submitLabel="Save changes"
        />
      )}

      {/* Staff list */}
      {loading ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : staff.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-[#E8E6E2] rounded-xl">
          <p className="text-sm text-[#6B6B6B]">No staff found</p>
          {canWrite && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-3 text-sm font-medium"
              style={{ color: ACCENT }}
            >
              Add your first member →
            </button>
          )}
        </div>
      ) : filterDuty ? (
        /* Flat list when duty filter active */
        <div className="bg-white border border-[#E8E6E2] rounded-xl overflow-hidden">
          {staff.map((m, i) => <StaffRow key={m.id} m={m} i={i} onEdit={openEdit} onToggle={toggleStatus} onDelete={setConfirmDeleteId} confirmDeleteId={confirmDeleteId} onConfirmDelete={handleDelete} onCancelDelete={() => setConfirmDeleteId(null)} canDelete={!!canDelete} canWrite={!!canWrite} />)}
        </div>
      ) : (
        /* Grouped by duty */
        <div className="space-y-6">
          {Object.entries(grouped).map(([duty, members]) => (
            <section key={duty}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DUTY_COLORS[duty] || 'bg-neutral-100 text-neutral-600'}`}>
                  {DUTY_MAP[duty] ?? duty}
                </span>
                <span className="text-xs text-[#A3A3A3]">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                <span className="text-xs text-[#A3A3A3]">· {DUTIES.find(d => d.value === duty)?.desc}</span>
              </div>
              <div className="bg-white border border-[#E8E6E2] rounded-xl overflow-hidden">
                {members.map((m, i) => (
                  <StaffRow key={m.id} m={m} i={i}
                    onEdit={openEdit}
                    onToggle={toggleStatus}
                    onDelete={setConfirmDeleteId}
                    confirmDeleteId={confirmDeleteId}
                    onConfirmDelete={handleDelete}
                    onCancelDelete={() => setConfirmDeleteId(null)}
                    canDelete={!!canDelete}
                    canWrite={!!canWrite}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

// Extracted row to keep JSX clean
const StaffRow: React.FC<{
  m: HubStaffMember;
  i: number;
  onEdit: (m: HubStaffMember) => void;
  onToggle: (m: HubStaffMember) => void;
  onDelete: (id: string) => void;
  confirmDeleteId: string | null;
  onConfirmDelete: (id: string) => void;
  onCancelDelete: () => void;
  canDelete: boolean;
  canWrite: boolean;
}> = ({ m, i, onEdit, onToggle, onDelete, confirmDeleteId, onConfirmDelete, onCancelDelete, canDelete, canWrite }) => {
  const DUTY_COLORS: Record<string, string> = {
    dryer: 'bg-orange-100 text-orange-700', sorter: 'bg-blue-100 text-blue-700',
    cherry_washer: 'bg-cyan-100 text-cyan-700', fermentation: 'bg-purple-100 text-purple-700',
    porter: 'bg-yellow-100 text-yellow-700', loader: 'bg-amber-100 text-amber-700',
    security: 'bg-red-100 text-red-700', supervisor: 'bg-green-100 text-green-700',
    general: 'bg-neutral-100 text-neutral-600',
  };
  const DUTY_MAP_LOCAL: Record<string, string> = {
    dryer: 'Dryer', sorter: 'Sorter', cherry_washer: 'Cherry Washer',
    fermentation: 'Fermentation', porter: 'Porter', loader: 'Loader',
    security: 'Security', supervisor: 'Supervisor', general: 'General',
  };
  const STATUS_COLORS: Record<string, string> = {
    active: 'bg-green-100 text-green-700', seasonal: 'bg-blue-100 text-blue-700',
    inactive: 'bg-neutral-100 text-neutral-500',
  };

  return (
    <div className={`border-b border-[#E8E6E2] last:border-0 px-4 py-3 flex items-center gap-4 ${i % 2 !== 0 ? 'bg-[#FAFAF8]' : ''}`}>
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-[#E8E6E2] flex items-center justify-center text-[#6B6B6B] text-xs font-bold flex-shrink-0">
        {m.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
      </div>

      {/* Name + contact */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-[#1A1A1A]">{m.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[m.status] || 'bg-neutral-100 text-neutral-500'}`}>
            {m.status}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium hidden sm:inline-block ${DUTY_COLORS[m.duty] || 'bg-neutral-100 text-neutral-600'}`}>
            {DUTY_MAP_LOCAL[m.duty] ?? m.duty}
          </span>
        </div>
        <div className="flex gap-3 mt-0.5 text-xs text-[#A3A3A3] flex-wrap">
          {m.phone && <span>{m.phone}</span>}
          {m.idNumber && <span>ID: {m.idNumber}</span>}
          {m.startDate && <span>Since {new Date(m.startDate).toLocaleDateString('en-KE', { month: 'short', year: 'numeric' })}</span>}
          {m.notes && <span className="truncate max-w-[200px]">{m.notes}</span>}
        </div>
      </div>

      {/* Actions */}
      {canWrite && (
        <div className="flex items-center gap-3 text-xs whitespace-nowrap flex-shrink-0">
          <button onClick={() => onEdit(m)} className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">Edit</button>
          <button onClick={() => onToggle(m)} className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">
            {m.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
          {canDelete && (
            confirmDeleteId === m.id ? (
              <span className="flex items-center gap-1.5">
                <span className="text-[#A3A3A3]">Remove?</span>
                <button onClick={() => onConfirmDelete(m.id)} className="text-red-600 font-semibold">Yes</button>
                <button onClick={onCancelDelete} className="text-[#6B6B6B]">No</button>
              </span>
            ) : (
              <button onClick={() => onDelete(m.id)} className="text-red-400 hover:text-red-600 transition-colors">Remove</button>
            )
          )}
        </div>
      )}
    </div>
  );
};
